/* eslint-disable max-lines */
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Pagination,
  Paper,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { capitalize, debounce, isNil } from 'lodash';

import LoadingModule from 'common/components/LoadingModule';
import { SortKey } from 'enums/common.enum';
import { extractNullLineItemsKey } from 'common/utils';

import DeleteIcon from '@mui/icons-material/Delete';
import BatchShippingIcon, { batchShippingTooltip } from 'common/components/BatchShippingIcon';
import BusinessAddressIndicator from 'common/components/BusinessAddressIndicator';
import {
  associateOrderShipment,
  deleteUnreconcilableOrders,
  getOrdersSuggestionsForShipstation,
  listUnReconciledOrdersByPage,
} from 'store/thunks/workQueueThunks';

import NothingFound from 'common/components/NothingFound';
import OrdersSearchField from 'common/forms/OrdersSearchField';
import { workQueueActions } from 'store/slices/workQueueSlice';
import { enqueueSnackbar } from 'notistack';
import ReconcileConfirmationDialog from './ReconcileConfirmationDialog';

const headCells = [
  {
    id: SortKey.PATIENT_NAME,
    label: 'Name',
    sortable: true,
  },
  {
    id: SortKey.ORDER_ID,
    label: 'Order Id',
    sortable: true,
  },
  {
    id: SortKey.ORDER_NUMBER,
    label: 'Order Number',
    sortable: true,
  },
  {
    id: SortKey.TRACKING_NUMBER,
    label: 'Tracking Number',
    sortable: true,
  },
  {
    id: SortKey.SERVICE_CODE,
    label: 'Service Code',
    sortable: false,
  },
  {
    id: SortKey.ADDRESS,
    label: 'Address',
    sortable: true,
  },
  {
    id: SortKey.SHIPPED_DATE,
    label: 'Ship Date',
    sortable: true,
  },
];

const OrderHeadCells = [
  {
    id: SortKey.PATIENT_NAME,
    label: 'Name',
    sortable: true,
  },
  {
    id: SortKey.ORDER_NUMBER,
    label: 'Order Number',
    sortable: true,
  },
  {
    id: SortKey.RX_NUMBER,
    label: 'Rx Number',
    sortable: true,
  },
];

const ReconcileShipmentTable = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [isDeleteButton, setIsDeleteButton] = useState(false);
  const [selectedSuggestedOrderIds, setSelectedSuggestedOrderIds] = useState([]);

  const dispatch = useDispatch();
  const {
    pages,
    currentPage,
    totalPages,
    deleteIrreconcileOrderItems,
    selectedReconcileId,
    suggestionsLoading,
    linkOrderLoading,
    deleteOrderLoading,
    filteredData,
    selectedShipstationOrderDetails,
    ordersSuggestionsCurrentPage,
    ordersSuggestionsCount,
    ordersSuggestionsTotalPages,
    searchInputValue,
  } = useSelector(({ workQueue }) => ({
    ...workQueue.reconcileShipment,
    totalPages: Math.ceil(workQueue.reconcileShipment.count / 25),
    ordersSuggestionsTotalPages: Math.ceil(workQueue.reconcileShipment.ordersSuggestionsCount / 25),
  }));
  const unReconciledOrders = pages[currentPage];

  const handlePageChange = (_, page) => {
    if (currentPage === page) {
      return;
    }
    dispatch(workQueueActions.clearSelectedIrreconcileOrders());
    dispatch(listUnReconciledOrdersByPage({ page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOrdersSuggestionsPageChange = (_, page) => {
    if (ordersSuggestionsCurrentPage === page) {
      return;
    }

    dispatch(workQueueActions.updateOrdersSuggestionsPage({ page }));

    const trimmedSearchValue = searchInputValue?.trim();
    if (trimmedSearchValue && trimmedSearchValue.length >= 3) {
      dispatch(
        getOrdersSuggestionsForShipstation({
          filter: { flatSearch: trimmedSearchValue },
          page,
        })
      ).then((response) => {
        const { result } = response?.getOrderSuggestions || response;
        dispatch(workQueueActions.setFilteredData(result));
        setSelectedSuggestedOrderIds([]);
      });
    } else if (selectedReconcileId && selectedShipstationOrderDetails) {
      const shipmentItems = selectedShipstationOrderDetails?.shipmentData?.shipmentItems;
      if (shipmentItems) {
        dispatch(
          getOrdersSuggestionsForShipstation({
            input: { lineItemKey: extractNullLineItemsKey(shipmentItems) },
            page,
          })
        ).then((response) => {
          const { result } = response?.getOrderSuggestions || response;
          dispatch(workQueueActions.setFilteredData(result));
          setSelectedSuggestedOrderIds([]);
        });
      }
    }
  };

  const debouncedFetchOrdersRef = useRef(null);

  useEffect(() => {
    debouncedFetchOrdersRef.current = debounce((input) => {
      const trimmed = input.trim();
      if (!trimmed || trimmed.length < 3) {
        setSelectedSuggestedOrderIds([]);
        dispatch(workQueueActions.clearOrdersSuggestionsPages());
        dispatch(workQueueActions.setFilteredData([]));
        return;
      }

      dispatch(workQueueActions.clearOrdersSuggestionsPages());
      dispatch(
        getOrdersSuggestionsForShipstation({
          filter: { flatSearch: trimmed },
          page: 1,
        })
      ).then((response) => {
        const { result } = response?.getOrderSuggestions || response;
        dispatch(workQueueActions.setFilteredData(result));
        setSelectedSuggestedOrderIds([]);
      });
    }, 500);

    return () => {
      debouncedFetchOrdersRef.current?.cancel();
    };
  }, [dispatch]);

  const handleSearchInput = useCallback((inputValue) => {
    debouncedFetchOrdersRef.current(inputValue);
  }, []);

  const handleRadioSelect = (reconcileId, shipmentItems, shipStationOrdeDetails) => {
    dispatch(workQueueActions.setFilteredData([]));
    setSelectedSuggestedOrderIds([]);
    dispatch(workQueueActions.setReconcileSearchInput(''));
    dispatch(workQueueActions.setReconcileSelectedReconcileId(reconcileId));
    dispatch(workQueueActions.setSelectedShipstationOrderDetails(shipStationOrdeDetails));
    dispatch(workQueueActions.clearOrdersSuggestionsPages());
    dispatch(
      getOrdersSuggestionsForShipstation({
        input: { lineItemKey: extractNullLineItemsKey(shipmentItems) },
        page: 1,
      })
    ).then((response) => {
      const { result } = response?.getOrderSuggestions || response;
      dispatch(workQueueActions.setFilteredData(result));
      setSelectedSuggestedOrderIds([]);
    });
  };

  const handleCheckboxChange = useCallback(
    (spOrderId) => {
      setSelectedSuggestedOrderIds([]);
      dispatch(workQueueActions.setFilteredData([]));
      dispatch(workQueueActions.setReconcileSearchInput(''));
      dispatch(workQueueActions.clearOrdersSuggestionsPages());
      dispatch(workQueueActions.toggleIrreconcileShipmentSelection(spOrderId));
    },
    [dispatch]
  );

  const handleSuggestedOrderCheckboxChange = useCallback(
    (lineItemId) => {
      dispatch(workQueueActions.clearSelectedIrreconcileOrders());
      setSelectedSuggestedOrderIds((prevSelected) =>
        prevSelected?.includes(lineItemId)
          ? prevSelected?.filter((id) => id !== lineItemId)
          : [...prevSelected, lineItemId]
      );
    },
    [dispatch]
  );

  const handleDeleteSelected = () => {
    dispatch(deleteUnreconcilableOrders({ spOrderIds: deleteIrreconcileOrderItems })).then(
      (res) => {
        if (res) {
          enqueueSnackbar('successfully deleted!', { variant: 'success' });
          setSelectedSuggestedOrderIds([]);
          dispatch(workQueueActions.setFilteredData([]));
          dispatch(workQueueActions.clearOrdersSuggestionsPages());
          dispatch(listUnReconciledOrdersByPage({ page: 1 }));
          dispatch(workQueueActions.clearSelectedIrreconcileOrders());
          setOpenDialog(false);
        }
      }
    );
  };

  const associateOrderHandler = () => {
    dispatch(workQueueActions.clearSelectedIrreconcileOrders());
    const selectedLineItemId = selectedSuggestedOrderIds[0];
    const SelectedOrderLineItemIds = selectedSuggestedOrderIds;
    const selectedOrder = filteredData?.find((order) =>
      order.lineItems?.some((item) => item?.lineItemId === selectedLineItemId)
    );

    const input = {
      orderShipmentId: selectedOrder?.orderShipmentId,
      spOrderId: selectedShipstationOrderDetails?.spOrderId,
      trackingNumber: selectedShipstationOrderDetails?.trackingNumber,
      carrierCode: selectedShipstationOrderDetails?.shipmentData?.carrierCode,
      orderId: selectedOrder?.orderId,
      lineItemIds: SelectedOrderLineItemIds,
    };

    dispatch(associateOrderShipment({ input }))
      .then(() => {
        setOpenDialog(false);
        enqueueSnackbar('Order linked successfully!', { variant: 'success' });
        dispatch(listUnReconciledOrdersByPage({ page: 1 }));
        dispatch(workQueueActions.clearReconcileFilterControls());
        dispatch(workQueueActions.setReconcileSearchInput(''));
        dispatch(workQueueActions.setFilteredData([]));
        dispatch(workQueueActions.clearOrdersSuggestionsPages());
        setSelectedSuggestedOrderIds([]);
      })
      .catch((error) => {
        const associateErrorMsg =
          error?.response?.errors?.[0]?.message ?? error?.message ?? 'Failed to link order';
        enqueueSnackbar(associateErrorMsg, { variant: 'error' });
        setOpenDialog(false);
      });
  };

  const isLinkButtonDisabled = selectedSuggestedOrderIds.length === 0;
  const isDeleteCheckboxDisabled = selectedSuggestedOrderIds.length > 0;

  return (
    <>
      <Grid container sx={{ mt: 2 }} alignItems='stretch'>
        <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
          <TableContainer component={Paper} sx={{ flex: 1 }}>
            <Table aria-label='shipments table'>
              <TableHead>
                <TableRow>
                  <TableCell
                    colSpan={9}
                    sx={{ textAlign: 'center', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}
                  >
                    <Typography variant='h6' fontWeight={600}>
                      ShipStation Orders
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }} />
                  {headCells?.map((value) => {
                    return (
                      <TableCell
                        key={value.id}
                        sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }}
                      >
                        {value?.label}
                      </TableCell>
                    );
                  })}
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#e0e0e0',
                      pl: 3,
                      py: 0,
                    }}
                  >
                    {deleteIrreconcileOrderItems?.length > 0 ? (
                      <Button
                        variant='contained'
                        color='error'
                        size='small'
                        onClick={() => {
                          setIsDeleteButton(true);
                          setOpenDialog(true);
                        }}
                        startIcon={<DeleteIcon />}
                        sx={{ scale: 1 }}
                      >
                        ({deleteIrreconcileOrderItems?.length})
                      </Button>
                    ) : (
                      <DeleteIcon sx={{ pr: 0, textAlign: 'center' }} />
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isNil(unReconciledOrders) && (
                  <TableRow>
                    <TableCell colSpan={9} align='center'>
                      <LoadingModule />
                    </TableCell>
                  </TableRow>
                )}
                {!isNil(unReconciledOrders) &&
                  (unReconciledOrders.length > 0 ? (
                    unReconciledOrders.map((row, index) => (
                      <TableRow key={row?.shipmentReconcileId ?? index}>
                        <TableCell padding='checkbox'>
                          <Radio
                            checked={selectedReconcileId === row?.shipmentReconcileId}
                            onChange={() =>
                              handleRadioSelect(
                                row?.shipmentReconcileId,
                                row?.shipmentData?.shipmentItems,
                                row
                              )
                            }
                            value={index}
                            name='shipment-radio'
                            inputProps={{ 'aria-label': `Select shipment ${index}` }}
                          />
                        </TableCell>
                        <TableCell>{row?.patientName}</TableCell>
                        <TableCell>{row?.spOrderId}</TableCell>
                        <TableCell>{row?.orderNumber}</TableCell>
                        <TableCell>{row?.shipmentData?.trackingNumber}</TableCell>
                        <TableCell>
                          {row?.shipmentData?.serviceCode
                            ? row.shipmentData.serviceCode.replace(/_/g, ' ')
                            : 'NA'}
                        </TableCell>
                        <TableCell>
                          {row?.shipmentData?.shipTo?.city && row?.shipmentData?.shipTo?.state
                            ? `${capitalize(row?.shipmentData?.shipTo?.city)} - ${
                                row?.shipmentData?.shipTo?.state
                              }`
                            : capitalize(
                                row?.shipmentData?.shipTo?.city || row?.shipmentData?.shipTo?.state
                              )}
                        </TableCell>
                        <TableCell>{row?.shipmentData?.shipDate}</TableCell>
                        <TableCell>
                          <Checkbox
                            checked={deleteIrreconcileOrderItems?.includes(row?.spOrderId)}
                            onChange={() => handleCheckboxChange(row?.spOrderId)}
                            disabled={isDeleteCheckboxDisabled}
                            inputProps={{ 'aria-label': `Select order ${row.id}` }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align='center'>
                        <NothingFound />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {!isNil(unReconciledOrders) && totalPages > 1 && (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={14} align='right'>
                      <Pagination
                        sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
                        count={totalPages}
                        page={currentPage}
                        size='small'
                        onChange={handlePageChange}
                      />
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Box>
          </TableContainer>
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <TableContainer component={Paper} sx={{ flex: 1 }}>
            <Table aria-label='Reconcile Orders table'>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: '#f5f5f5',
                      fontWeight: 'bold',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
                      <Typography variant='h6' fontWeight={600}>
                        Orders
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell colSpan={6} sx={{ backgroundColor: '#f5f5f5', p: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                      <OrdersSearchField onSearchInput={handleSearchInput} />
                    </Box>
                  </TableCell>
                </TableRow>
                {filteredData?.length > 0 && (
                  <TableRow sx={{ height: '50px' }}>
                    {OrderHeadCells.map((cells) => {
                      return (
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }}>
                          {cells?.label}
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }} />
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }}>
                      <Button
                        disabled={isLinkButtonDisabled}
                        sx={{ p: 0, m: 0 }}
                        variant='contained'
                        size='small'
                        color='success'
                        onClick={() => {
                          setOpenDialog(true);
                          setIsDeleteButton(false);
                        }}
                      >
                        Link
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableHead>
              {suggestionsLoading ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={5} align='center'>
                      <LoadingModule />
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {filteredData?.length > 0 ? (
                    filteredData?.map((row) => {
                      const lineItemId = row.lineItems[0]?.lineItemId;
                      return (
                        <TableRow key={lineItemId}>
                          <TableCell>
                            {row?.patient?.lastName} {row?.patient?.firstName}
                          </TableCell>
                          <TableCell>{row?.orderId}</TableCell>
                          <TableCell>{row?.lineItems?.map((items) => items?.rxNumber)}</TableCell>
                          <TableCell>
                            {row?.lineItems?.map((item) =>
                              item?.inBatchShipmentQueue ? (
                                <Tooltip
                                  title={batchShippingTooltip({
                                    isBusinessAddress: item?.isBusinessAddress,
                                  })}
                                  arrow
                                >
                                  <BatchShippingIcon showBadge={item?.isBusinessAddress} />
                                </Tooltip>
                              ) : (
                                item?.isBusinessAddress && <BusinessAddressIndicator />
                              )
                            )}
                          </TableCell>
                          <TableCell>
                            <Checkbox
                              checked={selectedSuggestedOrderIds?.includes(lineItemId)}
                              onChange={() => handleSuggestedOrderCheckboxChange(lineItemId)}
                              inputProps={{ 'aria-label': `Select order ${lineItemId}` }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align='center'>
                        <NothingFound />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              )}
              {!suggestionsLoading && ordersSuggestionsCount > 25 && (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={5} align='right'>
                      <Pagination
                        sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
                        count={ordersSuggestionsTotalPages}
                        page={ordersSuggestionsCurrentPage}
                        size='small'
                        onChange={handleOrdersSuggestionsPageChange}
                      />
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      {openDialog && (
        <ReconcileConfirmationDialog
          isDeleteButton={isDeleteButton}
          openDialog={openDialog}
          setOpenDialog={setOpenDialog}
          handleDeleteSelected={handleDeleteSelected}
          associateOrderHandler={associateOrderHandler}
          linkOrderLoading={linkOrderLoading}
          deleteOrderLoading={deleteOrderLoading}
        />
      )}
    </>
  );
};

export default ReconcileShipmentTable;
